import { uniqueId } from 'lodash';
import React, { useMemo } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { InfiniteData } from 'react-query';
import { Link } from 'react-scroll';

import articlesIcon from 'images/directory/articles_icon.svg';
import avatarIcon from 'images/directory/avatar_icon.svg';
import avatarIconHover from 'images/directory/avatar_icon_hover.svg';
import commentIcon from 'images/directory/comment_icon.svg';
import commentIconHover from 'images/directory/comment_icon_hover.svg';
import forumIcon from 'images/directory/forum_icon.svg';
import forumIconHover from 'images/directory/forum_icon_hover.svg';
import likesIcon from 'images/directory/likes_icon.svg';
import likesIconHover from 'images/directory/likes_icon_hover.svg';
import openToIcon from 'images/directory/opento_icon.svg';
import openToIconHover from 'images/directory/opento_icon_hover.svg';
import skillIcon from 'images/directory/skill_icon.svg';
import skillIconHover from 'images/directory/skill_icon_hover.svg';
import topicIcon from 'images/directory/topic_icon.svg';
import topicIconHover from 'images/directory/topic_icon_hover.svg';
import weekendIcon from 'images/directory/weekend_icon.svg';
import weekendIconHover from 'images/directory/weekend_icon_hover.svg';

import Comment from '@components/Comment';
import Feed from '@components/Feed';
import Spinner from '@components/Spinner';
import { ForumPost } from '@utils/backend-api/forumPosts';
import {
  LayoutProfile,
  ProfileEntity,
  SubscriberCommentsResponse,
} from '@utils/backend-api/subscriber_directory';

import ForumPostList from '../Forum/ForumPostList';
import GatedContent from '../Forum/GatedContent';
import OpenToSection from './OpenToSection';
import Section from './Section';

interface LayoutProps extends LayoutProfile {
  username: string;
  books: ProfileEntity[];
  commentsData: InfiniteData<SubscriberCommentsResponse>;
  commentLikesData: InfiniteData<SubscriberCommentsResponse>;
  fetchMoreCommentLikes: () => void;
  fetchMoreComments: () => void;
  forumPosts: ForumPost[];
  hasMoreComments: boolean;
  hasMoreCommentLikes: boolean;
  isFetchingMoreComments: boolean;
  isFetchingMoreCommentLikes: boolean;
  shows: ProfileEntity[];
  sportsToPlay: ProfileEntity[];
  sportsToWatch: ProfileEntity[];
  vacationSpots: ProfileEntity[];
  canViewFullProfile: boolean;
}

const Layout = ({
  username,
  bio,
  books,
  commentsData,
  commentLikesData,
  fetchMoreCommentLikes,
  fetchMoreComments,
  findingNewJob,
  findingNewJobDesc,
  forumPosts,
  hasMoreComments,
  hasMoreCommentLikes,
  hiring,
  hiringDesc,
  investingInStartups,
  investingInStartupsDesc,
  isFetchingMoreComments,
  isFetchingMoreCommentLikes,
  isCurrentUserLead,
  isOwnProfile,
  name,
  providingServices,
  providingServicesDesc,
  shows,
  sportsToPlay,
  sportsToWatch,
  skills,
  topics,
  vacationSpots,
  canViewFullProfile,
  contributorContent,
  contributorContentPagination,
}: LayoutProps) => {
  const NON_WEEKEND_SECTIONS = [
    {
      sectionHeader: 'Topics',
      entities: topics,
      param: 'topics',
      pluralFieldName: 'Topics',
      fieldName: 'Topic',
      color: 'red',
    },
    {
      sectionHeader: 'Skills',
      entities: skills,
      param: 'skills',
      pluralFieldName: 'Skills',
      fieldName: 'Skill',
      color: 'purple',
    },
  ];
  const WEEKEND_SECTIONS = [
    {
      sectionSubHeader: 'Favorite Vacation Spot',
      entities: vacationSpots,
      param: 'favorite_vacation_spots',
      pluralFieldName: 'Vacation Spots',
      fieldName: 'Vacation Spot',
      color: 'blue',
    },
    {
      sectionSubHeader: 'Favorite Sports to Play',
      entities: sportsToPlay,
      param: 'favorite_sport_play',
      pluralFieldName: 'Sports to Play',
      fieldName: 'Sport to Play',
      color: 'blue',
    },
    {
      sectionSubHeader: 'Favorite Sports to Watch',
      entities: sportsToWatch,
      param: 'favorite_sport_watch',
      pluralFieldName: 'Sports to Watch',
      fieldName: 'Sport to Watch',
      color: 'blue',
    },
    {
      sectionSubHeader: 'Favorite Books',
      entities: books,
      param: 'favorite_book',
      pluralFieldName: 'Favorite Books',
      fieldName: 'Favorite Book',
      color: 'blue',
    },
    {
      sectionSubHeader: 'Favorite TV Shows / Movies',
      entities: shows,
      param: 'favorite_show',
      pluralFieldName: 'TV Shows / Movies',
      fieldName: 'TV Show / Movie',
      color: 'blue',
    },
  ];

  const OPEN_TO_LIST = [
    {
      openTo: investingInStartups,
      openToParams: 'investing_in_startups',
      openToTitle: 'Looking to invest in startups / private companies',
      openToDesc: investingInStartupsDesc,
    },
    {
      openTo: hiring,
      openToParams: 'hiring',
      openToTitle: 'Looking to hire team members',
      openToDesc: hiringDesc,
    },
    {
      openTo: findingNewJob,
      openToParams: 'finding_new_job',
      openToTitle: 'Looking for a job',
      openToDesc: findingNewJobDesc,
    },
    {
      openTo: providingServices,
      openToParams: 'providing_services',
      openToTitle: 'Looking to provide services',
      openToDesc: providingServicesDesc,
    },
  ];

  const numComments = useMemo(() => {
    const commentsPages = commentsData?.pages;
    return commentsPages ? commentsPages[0]?.comments.length : 0;
  }, [commentsData]);

  const numCommentLikes = useMemo(() => {
    const commentLikes = commentLikesData?.pages;
    return commentLikes ? commentLikes[0]?.comments.length : 0;
  }, [commentLikesData]);

  return (
    <Container className="Layout">
      <Row>
        <Col className="col-sm-12 col-md-2 profile-detail-nav">
          <ul className="profile-nav">
            <li>
              <div className="profile-nav-icon">
                <img src={avatarIcon} className="without-hover-state" />
                <img src={avatarIconHover} className="with-hover-state" />
              </div>
              <Link className="link-nav" activeClass="active" to="About" spy={true} smooth={true}>
                About
              </Link>
            </li>
            {(investingInStartups || hiring || findingNewJob || providingServices) && (
              <li>
                <div className="profile-nav-icon">
                  <img src={openToIcon} className="without-hover-state" />
                  <img src={openToIconHover} className="with-hover-state" />
                </div>
                <Link className="link-nav" activeClass="active" to="About" spy={true} smooth={true}>
                  Open to
                </Link>
              </li>
            )}
            {!!contributorContent?.length && (
              <li>
                <div className="profile-nav-icon">
                  <img src={articlesIcon} />
                </div>
                <Link
                  className="link-nav"
                  activeClass="active"
                  to="Articles"
                  spy={true}
                  smooth={true}
                >
                  Articles
                </Link>
              </li>
            )}
            {topics?.length > 0 && (
              <li>
                <div className="profile-nav-icon">
                  <img src={topicIcon} className="without-hover-state" />
                  <img src={topicIconHover} className="with-hover-state" />
                </div>
                <Link
                  className="link-nav"
                  activeClass="active"
                  to="Topics"
                  spy={true}
                  smooth={true}
                >
                  Topics
                </Link>
              </li>
            )}
            {skills?.length > 0 && (
              <li>
                <div className="profile-nav-icon">
                  <img src={skillIcon} className="without-hover-state" />
                  <img src={skillIconHover} className="with-hover-state" />
                </div>
                <Link
                  className="link-nav"
                  activeClass="active"
                  to="Skills"
                  spy={true}
                  smooth={true}
                >
                  Skills
                </Link>
              </li>
            )}
            {(vacationSpots?.length > 0 ||
              sportsToPlay?.length > 0 ||
              sportsToWatch?.length > 0 ||
              books?.length > 0 ||
              shows?.length > 0) && (
              <li>
                <div className="profile-nav-icon">
                  <img src={weekendIcon} className="without-hover-state" />
                  <img src={weekendIconHover} className="with-hover-state" />
                </div>
                <Link
                  className="link-nav"
                  activeClass="active"
                  to="Weekend"
                  spy={true}
                  smooth={true}
                >
                  Weekend
                </Link>
              </li>
            )}
            {forumPosts?.length > 0 && (
              <li>
                <div className="profile-nav-icon">
                  <img src={forumIcon} className="without-hover-state" />
                  <img src={forumIconHover} className="with-hover-state" />
                </div>
                <Link
                  className="link-nav"
                  activeClass="active"
                  to="ForumPosts"
                  spy={true}
                  smooth={true}
                >
                  Forum posts
                </Link>
              </li>
            )}

            {numComments > 0 && (
              <li>
                <div className="profile-nav-icon">
                  <img src={commentIcon} className="without-hover-state" />
                  <img src={commentIconHover} className="with-hover-state" />
                </div>
                <Link
                  className="link-nav"
                  activeClass="active"
                  to="Comments"
                  spy={true}
                  smooth={true}
                >
                  Comments
                </Link>
              </li>
            )}
            {numCommentLikes > 0 && (
              <li>
                <div className="profile-nav-icon">
                  <img src={likesIcon} className="without-hover-state" />
                  <img src={likesIconHover} className="with-hover-state" />
                </div>
                <Link className="link-nav" activeClass="active" to="Likes" spy={true} smooth={true}>
                  Likes
                </Link>
              </li>
            )}
          </ul>
        </Col>
        <Col className="col-sm-12 col-md-10 profile-detail-wrapper">
          <div className="section-container" id="About">
            <div className="section-header">About</div>
            <div className={`section-detail ${!canViewFullProfile ? 'blur-bio' : ''}`}>{bio}</div>
            {!canViewFullProfile && !isOwnProfile && (
              <GatedContent
                headline={
                  isCurrentUserLead ? 'Join The Information' : 'Join now to view the full profile'
                }
                showTagline={isCurrentUserLead}
                tagline={`${name} and other subscribers to The Information have access to our private directory
                of people serious about technology. Subscribe to access The Information’s
                network along with our exclusive journalism covering the technology industry.`}
              />
            )}
          </div>
          {canViewFullProfile && (
            <>
              {(investingInStartups || hiring || findingNewJob || providingServices) && (
                <div className="section-container" id="Open-To">
                  <div className="section-header">Open To</div>
                  <div className="section-detail">
                    {OPEN_TO_LIST.map(
                      (section, id) =>
                        section.openTo && (
                          <OpenToSection
                            key={id}
                            openToParams={section.openToParams}
                            openToTitle={section.openToTitle}
                            openToDesc={section.openToDesc}
                          />
                        )
                    )}
                  </div>
                </div>
              )}
              {!!contributorContent?.length && (
                <div className="section-container" id="Articles">
                  <div className="section-header">Articles</div>
                  <div className="section-detail">
                    <Feed
                      articlesOrBriefings={contributorContent}
                      paginationData={contributorContentPagination}
                      username={username}
                    />
                  </div>
                </div>
              )}
              {NON_WEEKEND_SECTIONS.map(
                (section, id) =>
                  section.entities.length > 0 && (
                    <div className="section-container" id={section.sectionHeader} key={id}>
                      <div className="section-header">{section.sectionHeader}</div>
                      <Section
                        color={section.color}
                        entities={section.entities}
                        param={section.param}
                        fieldName={section.fieldName}
                        pluralFieldName={section.pluralFieldName}
                        profileName={name}
                      />
                    </div>
                  )
              )}
              {(vacationSpots.length > 0 ||
                sportsToPlay.length > 0 ||
                sportsToWatch.length > 0 ||
                books.length > 0 ||
                shows.length > 0) && (
                <div className="section-container" id="Weekend">
                  <div className="section-header">Weekend</div>
                  <div className="section-detail">
                    {WEEKEND_SECTIONS.map(
                      (section, id) =>
                        section.entities.length > 0 && (
                          <div className="weekend-container weekend" key={id}>
                            <div className="weekend-header">{section.sectionSubHeader}</div>
                            <Section
                              entities={section.entities}
                              param={section.param}
                              fieldName={section.fieldName}
                              pluralFieldName={section.pluralFieldName}
                              profileName={name}
                              color={section.color}
                            />
                          </div>
                        )
                    )}
                  </div>
                </div>
              )}
              {forumPosts?.length > 0 && (
                <div className="section-container" id="ForumPosts">
                  <div className="section-header">Forum Posts</div>
                  <ForumPostList forumPosts={forumPosts} />
                </div>
              )}
              {numComments > 0 && (
                <div className="section-container" id="Comments">
                  <div className="section-header">Article Comments</div>
                  <div className="section-detail with-comments">
                    {commentsData.pages.map((group) =>
                      group.comments.map((comment) => <Comment {...comment} key={uniqueId()} />)
                    )}
                    {hasMoreComments && (
                      <div className="show-more">
                        {isFetchingMoreComments ? (
                          <Spinner />
                        ) : (
                          <Button
                            onClick={fetchMoreComments}
                            className="profile-button --grey fetch-btn"
                          >
                            Show More
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {numCommentLikes > 0 && (
                <div className="section-container" id="Likes">
                  <div className="section-header">Article Comment Likes</div>
                  <div className="section-detail with-comments">
                    {commentLikesData.pages.map((group) =>
                      group.comments.map((comment) => <Comment {...comment} key={uniqueId()} />)
                    )}
                    {hasMoreCommentLikes && (
                      <div className="show-more">
                        {isFetchingMoreCommentLikes ? (
                          <Spinner />
                        ) : (
                          <Button
                            onClick={fetchMoreCommentLikes}
                            className="profile-button --grey fetch-btn"
                          >
                            Show More
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default Layout;
